import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Phone,
  MessageSquare,
  Settings,
  GraduationCap,
  Headphones,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function HowItWorksPage() {
  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="how-it-works-page">
      <SEO
        title="How It Works"
        description="See how easy it is to get started with Momentum VA — from discovery call to onboarding. We handle hiring, training, and management of your dedicated real estate virtual assistant."
        path="/how-it-works"
      />
      {/* Hero Section */}
      <section className="relative bg-[#0B1C3E] py-20 lg:py-28" data-testid="hiw-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/3 w-72 h-72 bg-[#2A9D8F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/3 w-72 h-72 bg-[#E86C3F] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              How It Works
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6 font-['Outfit']">
              Simple Process, Seamless Support
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Getting started with Momentum VA is straightforward. We've designed 
              our process to be thorough yet efficient, ensuring you get the right 
              support from day one.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 md:py-28 bg-white" data-testid="process-steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              Four Steps to Better Support
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              From first conversation to fully operational support, here's how 
              we get you up and running.
            </p>
          </div>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center" data-testid="step-1">
              <div>
                <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center">
                      <Phone className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-5xl font-bold text-slate-200 font-['Outfit']">01</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
                    Discovery Call
                  </h3>
                  <p className="text-slate-600 text-lg mb-6">
                    We start with a conversation to understand your office, your challenges, 
                    and your goals. No sales pitch—just a genuine discussion about how 
                    Momentum VA can best support your business and operational needs.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Understanding your current workload</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Identifying pain points and priorities</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Discussing your ideal support structure</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[#2A9D8F] flex items-center justify-center">
                  <Phone className="w-10 h-10 text-white" />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center" data-testid="step-2">
              <div className="hidden lg:flex items-center justify-center lg:order-1">
                <div className="w-20 h-20 rounded-full bg-[#5D6D35] flex items-center justify-center">
                  <Settings className="w-10 h-10 text-white" />
                </div>
              </div>
              <div className="lg:order-2">
                <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-5xl font-bold text-slate-200 font-['Outfit']">02</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
                    Understanding Your Systems
                  </h3>
                  <p className="text-slate-600 text-lg mb-6">
                    We take time to learn your software, processes, and preferences. 
                    This ensures our support integrates seamlessly with how you already work.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Learning your property management software</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Understanding your workflows and processes</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Documenting your preferences and standards</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center" data-testid="step-3">
              <div>
                <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center">
                      <GraduationCap className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-5xl font-bold text-slate-200 font-['Outfit']">03</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
                    Matching & Training
                  </h3>
                  <p className="text-slate-600 text-lg mb-6">
                    We carefully match you with a virtual assistant whose skills and experience 
                    align with your needs, then provide comprehensive training on your specific requirements.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Selecting the best-fit team member</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Training on your systems and processes</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Role-specific real estate education</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[#E86C3F] flex items-center justify-center">
                  <GraduationCap className="w-10 h-10 text-white" />
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid lg:grid-cols-2 gap-8 items-center" data-testid="step-4">
              <div className="hidden lg:flex items-center justify-center lg:order-1">
                <div className="w-20 h-20 rounded-full bg-[#0B1C3E] flex items-center justify-center">
                  <Headphones className="w-10 h-10 text-white" />
                </div>
              </div>
              <div className="lg:order-2">
                <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-[#0B1C3E] flex items-center justify-center">
                      <Headphones className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-5xl font-bold text-slate-200 font-['Outfit']">04</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
                    Ongoing Support
                  </h3>
                  <p className="text-slate-600 text-lg mb-6">
                    Your VA begins work, fully managed by our team. We handle performance, 
                    provide regular reporting, and continuously improve processes.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Dedicated team leader oversight</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Regular performance reporting</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Scalable support as you grow</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Message */}
      <section className="py-20 bg-[#5D6D35]" data-testid="key-message-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageSquare className="w-16 h-16 text-white/30 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
            We Manage. You Focus.
          </h2>
          <p className="text-white/90 text-lg leading-relaxed max-w-2xl mx-auto">
            The biggest difference with Momentum VA? You don't manage another employee. 
            We handle recruitment, training, supervision, performance, and leave cover. 
            You simply get reliable support, every day.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="faqs-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              FAQs
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              Common Questions
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="faq-0">
              <h3 className="text-lg font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">
                Do I need to manage the virtual assistant?
              </h3>
              <p className="text-slate-600">
                No. Momentum VA manages your virtual assistant completely. We handle supervision, 
                performance reviews, leave cover, and everything else. You simply assign tasks and receive results.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="faq-1">
              <h3 className="text-lg font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">
                What hours do your VAs work?
              </h3>
              <p className="text-slate-600">
                Our team works during Australian business hours. We align our schedules with your 
                time zone to ensure seamless communication and real-time support when you need it.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="faq-2">
              <h3 className="text-lg font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">
                How quickly can we get started?
              </h3>
              <p className="text-slate-600">
                Most clients are up and running within 2-3 weeks. This includes the discovery process, 
                matching, training, and onboarding to ensure a smooth start.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="faq-3">
              <h3 className="text-lg font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">
                What if we need to scale up or down?
              </h3>
              <p className="text-slate-600">
                Flexibility is built into our model. We can adjust your support level as your business 
                needs change—whether that's adding more hours, more VAs, or different services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0B1C3E]" data-testid="hiw-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
            Ready to Take the First Step?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Book your free discovery call today. We'll discuss your needs, answer 
            your questions, and help you decide if Momentum VA is right for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg"
                data-testid="hiw-cta-book-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                Book Your Discovery Call
              </Button>
            </Link>
            <Link to="/services">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                data-testid="hiw-cta-services"
              >
                Explore Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
